import { FunctionComponent, useState, useEffect, useMemo } from 'react'
import { RouteComponentProps, useHistory } from 'react-router-dom'

import Page from '../../domain/Page/Page'

import WizardComponent from '../component/wizard/WizardComponent'
import HeaderComponent from '../component/header/HeaderComponent'
import FooterComponent from '../component/footer/FooterComponent'
import BlocksComponent from '../component/block/BlocksComponent'

// Redux
import { useAppDispatch, useAppSelector } from '../../store/hook'
import {
  setAllPages,
  updateNextPageId,
  updateNextPageIdAndCurrentPageIddByGoBack,
  updateProgressValueByIdGoBack,
} from '../../store/wizzard/wizzardStep'

import FindAllPagesUseCase from '../../domain/Page/UseCase/FindAllPagesUseCase'
import FetchPageGateway from '../../gateway/Page/FetchPageGateway'
import storage from '../util/storage'
import Back from '../component/svg/Back'
import { useTranslation } from 'react-i18next'
import ReferentialGateway from '../../domain/Referential/ReferentialGateway'
import GetReferential from '../../useCase/Referential/GetReferential'
import InvalidElement from '../../domain/Folder/InvalidElementInterface'
import FooterCorrection from '../component/integrationState/FooterCorrection'
import LocalStorageGateway from '../../gateway/Form/LocalStorageGateway'
import { useFolderId } from '../../infrastructure/hooks'

type Params = {
  pageId: string
  blockId: string
  dossierId: string
}

const Form: FunctionComponent<RouteComponentProps<Params>> = ({ match }) => {
  const history = useHistory()
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const { folderId, isFromUrl } = useFolderId()

  const [pages, setPages] = useState<Page[] | null>(null)
  const [showBackButton, setShowBackButton] = useState<boolean>(false)
  const reduxPages = useAppSelector((state) => state.wizzardStep.pages)
  const reduxNextPageId = useAppSelector((state) => state.wizzardStep.nextPageId)
  const [invalidElements, setInvalidElements] = useState<InvalidElement[]>([]);

  useEffect(() => {
    const refentialGateway = new ReferentialGateway()
    const getRefential = new GetReferential(refentialGateway)
    getRefential.execute(storage.getFormId(folderId), null, folderId)

    if (!folderId) {
      history.push('/dossiers')
      return
    }

    // Sync to localStorage for DocuSign compatibility
    if (isFromUrl && folderId) {
      storage.setFolderId(folderId)
    }
  }, [history, folderId, isFromUrl])

  useEffect(() => {
    setInvalidElements(storage.getInvalidElements(folderId))
  }, [folderId])

  useEffect(() => {
    const loadElements = () => {
      setInvalidElements(storage.getInvalidElements(folderId));
    };
    window.addEventListener('invalidElementsUpdate', loadElements);

    return () => {
      window.removeEventListener('invalidElementsUpdate', loadElements);
    };
  }, [folderId]);

  useMemo(() => {
    const findAllPagesUseCase = new FindAllPagesUseCase(new FetchPageGateway(folderId))

    findAllPagesUseCase.execute().then((pages) => {
      if (JSON.stringify(reduxPages) !== JSON.stringify(pages)) {
        dispatch(setAllPages(JSON.stringify(pages)))
      }

      const initialPageId = match.params.pageId ?? pages[0].id
      if (JSON.stringify(reduxNextPageId) !== JSON.stringify(initialPageId)) {
        dispatch(updateNextPageId({ id: initialPageId }))
      }

      if (!match.params.pageId && pages.length > 0 && match.params.pageId !== pages[0].id) {
        // Use URL with folderId if available
        const basePath = isFromUrl && folderId ? `/dossier/${folderId}/form` : '/form'
        history.replace(`${basePath}/${pages[0].id}`)
      }

      setPages((prev) => (JSON.stringify(prev) !== JSON.stringify(pages) ? pages : prev))
    })
  }, [dispatch, history, match.params.pageId])

  useEffect(() => {
    if (!pages) {
      return
    }

    const dossierState = storage.getDossierState(folderId)
    setShowBackButton(
      pages[0].id !== match.params.pageId &&
      dossierState !== 'signed' &&
      dossierState !== 'signature'
    )
  }, [pages, match.params.pageId, folderId])

  const handleBackButtonClick = () => {
    dispatch(updateProgressValueByIdGoBack({ id: null }))
    dispatch(updateNextPageIdAndCurrentPageIddByGoBack({ id: match.params.pageId }))
    history.goBack()
  }

  const removeInvalidElementById = (
    invalidElements: InvalidElement[],
    targetElementId: string
  ): InvalidElement[] => {
    if (!invalidElements) return [];

    const updatedElements = invalidElements.filter(
      element => element.elementId !== targetElementId
    );

    return updatedElements;
  };

  const handleChangeInvalidElements = (data: Record<string, any>) => {
    let newInvalidElements = [...invalidElements];

    Object.keys(data).forEach(elementId => {
      const newSubmittedValue = data[elementId];
      const existingError = newInvalidElements.find(err => err.elementId === elementId);

      if (existingError) {
        const storedValueInError = existingError.value;

        if (storedValueInError !== newSubmittedValue) {
          newInvalidElements = removeInvalidElementById(newInvalidElements, elementId);
        }
      }
    });
    new LocalStorageGateway(folderId).saveInvalidElements(newInvalidElements)
    setInvalidElements(newInvalidElements);
  }

  return (
    <>
      {pages && match.params.pageId && (
        <div>
          <HeaderComponent />
          <div className="wrap u-mxAuto">
            {showBackButton && (
              <a href="#" onClick={handleBackButtonClick} className="back">
                <Back color="var(--ink-1)" />
                <span className="u-txt-size-s u-txt-color-ink-1 u-mxs">{t('button.back')}</span>
              </a>
            )}
          </div>
          <WizardComponent pages={pages} currentPageId={match.params.pageId} />
          <BlocksComponent
            pageId={match.params.pageId}
            blockId={match.params.blockId || null}
            invalidElements={invalidElements}
            handleInvalidElements={handleChangeInvalidElements}
          />
          {storage.hasToReSignValue(folderId) &&
            <FooterCorrection
              pages={pages}
              invalidElements={invalidElements}
            />
          }
          <FooterComponent />
        </div>
      )}
    </>
  )
}

export default Form
